import { useCallback, useMemo } from 'react';
import axios from 'axios';

export const useInboxData = ({
  user,
  BACKEND_URL,
  upcomingBookings,
  clientAcceptedQuickCuts,
  bookingHistory,
  barberBookings,
  barberAcceptedQuickCuts,
  barberCompletedQuickCuts,
  barberAdminChats,
  chatUnreadRef,
  setInboxItems,
  setInboxLoading
}) => {
  const chatTargets = useMemo(() => {
    if (!user) return [];

    if (user.user_type === 'client') {
      const bookingTargets = (upcomingBookings || [])
        .filter((b) => ['pending', 'accepted'].includes(b.status))
        .map((b) => ({
          id: b.id,
          type: 'booking',
          status: b.status,
          service: b.service,
          date: b.date || b.created_at,
          price: b.price,
          barbershop: b.barbershop,
          barber: b.barber
        }))
        .filter((b) => b.id);

      const quickCutTargets = (clientAcceptedQuickCuts || [])
        .filter((q) => q.status === 'accepted')
        .map((q) => ({
          id: q.id,
          type: 'quick_cut',
          status: q.status,
          service: q.service,
          date: q.created_at || q.date,
          price: q.price || q.accepted_price || q.max_price,
          barbershop: q.barbershop,
          barber: q.barber
        }))
        .filter((q) => q.id);

      return [...bookingTargets, ...quickCutTargets];
    }

    const bookingTargets = (barberBookings || [])
      .filter((b) => b.status === 'accepted')
      .map((b) => ({
        id: b.id,
        type: 'booking',
        status: b.status,
        service: b.service,
        date: b.date || b.created_at,
        price: b.price,
        barber: { id: b.client_id, name: b.client_name }
      }))
      .filter((b) => b.id);

    const quickCutTargets = (barberAcceptedQuickCuts || [])
      .filter((q) => q.status === 'accepted' || !q.status)
      .map((q) => ({
        id: q.id,
        type: 'quick_cut',
        status: q.status || 'accepted',
        service: q.service,
        date: q.created_at || q.date,
        price: q.price || q.accepted_price || q.max_price,
        client_id: q.client_id,
        client_name: q.client_name,
        barber: { id: q.client_id, name: q.client_name }
      }))
      .filter((q) => q.id);

    const adminTargets = (barberAdminChats || []).map((target) => ({
      id: target.service_id,
      type: 'admin',
      service: `Administración · ${target.barbershop?.name || 'Barbería'}`,
      date: target.latest_at || new Date().toISOString(),
      barbershop: target.barbershop,
      admin_message: true,
      barber: { id: 'admin', name: 'Administración' }
    }));

    return [...bookingTargets, ...quickCutTargets, ...adminTargets];
  }, [
    user,
    upcomingBookings,
    clientAcceptedQuickCuts,
    barberBookings,
    barberAcceptedQuickCuts,
    barberAdminChats
  ]);

  const inboxTargets = useMemo(() => {
    if (!user) return [];
    if (user.user_type === 'client') {
      const upcoming = (upcomingBookings || []).map((b) => ({
        id: b.id,
        type: 'booking',
        status: b.status,
        service: b.service,
        date: b.date || b.created_at,
        price: b.price,
        barbershop: b.barbershop,
        barber: b.barber
      }));
      const acceptedQuick = (clientAcceptedQuickCuts || []).map((q) => ({
        id: q.id,
        type: 'quick_cut',
        status: q.status,
        service: q.service,
        date: q.created_at || q.date,
        price: q.price || q.accepted_price || q.max_price,
        barbershop: q.barbershop,
        barber: q.barber
      }));
      const historyTargets = (bookingHistory || [])
        .filter((h) => h.status === 'completed' || h.status === 'cancelled')
        .map((h) => ({
          id: h.id,
          type: h.type === 'quick_cut' ? 'quick_cut' : 'booking',
          status: h.status,
          service: h.service || h.type_label || 'Servicio',
          date: h.date || h.created_at,
          price: h.price,
          barbershop: h.barbershop,
          barber: h.barber
        }));

      return [...upcoming, ...acceptedQuick, ...historyTargets].filter((t) => t.id);
    }

    const bookingTargets = (barberBookings || [])
      .map((b) => ({
        id: b.id,
        type: 'booking',
        status: b.status,
        service: b.service,
        date: b.date || b.created_at,
        price: b.price,
        barber: { id: b.client_id, name: b.client_name }
      }))
      .filter((b) => b.id);

    const quickCutTargets = [
      ...(barberAcceptedQuickCuts || []),
      ...(barberCompletedQuickCuts || [])
    ].map((q) => ({
      id: q.id,
      type: 'quick_cut',
      status: q.status || 'accepted',
      service: q.service,
      date: q.created_at || q.date,
      price: q.price || q.accepted_price || q.max_price,
      client_id: q.client_id,
      client_name: q.client_name,
      barber: { id: q.client_id, name: q.client_name }
    }));

    const adminTargets = (barberAdminChats || []).map((target) => ({
      id: target.service_id,
      type: 'admin',
      status: 'admin',
      service: `Administración · ${target.barbershop?.name || 'Barbería'}`,
      date: target.latest_at || new Date().toISOString(),
      barbershop: target.barbershop,
      admin_message: true,
      barber: { id: 'admin', name: 'Administración' }
    }));

    return [...bookingTargets, ...quickCutTargets, ...adminTargets].filter((t) => t.id);
  }, [
    user,
    upcomingBookings,
    clientAcceptedQuickCuts,
    bookingHistory,
    barberBookings,
    barberAcceptedQuickCuts,
    barberCompletedQuickCuts,
    barberAdminChats
  ]);

  const loadInbox = useCallback(async () => {
    if (!user || !BACKEND_URL) return;
    setInboxLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const results = await Promise.all(
        inboxTargets.map(async (target) => {
          try {
            const response = await axios.get(`${BACKEND_URL}/api/chat/${target.id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const messages = response.data.messages || [];
            const lastMessage = messages[messages.length - 1];
            const targetKey = `${target.type}:${target.id}`;
            return {
              ...target,
              lastMessage,
              unreadCount: chatUnreadRef.current[targetKey] || 0
            };
          } catch (error) {
            return { ...target, lastMessage: null, unreadCount: 0 };
          }
        })
      );
      results.sort((a, b) => {
        const aTime = new Date(a.lastMessage?.timestamp || a.date || 0).getTime();
        const bTime = new Date(b.lastMessage?.timestamp || b.date || 0).getTime();
        return bTime - aTime;
      });
      setInboxItems(results);
    } finally {
      setInboxLoading(false);
    }
  }, [BACKEND_URL, chatUnreadRef, inboxTargets, setInboxItems, setInboxLoading, user]);

  return { chatTargets, inboxTargets, loadInbox };
};
